import { inject, Injectable } from "@angular/core";

import { defer, Observable, of } from "rxjs";
import { catchError, finalize, map, shareReplay } from "rxjs/operators";

import { APP_SHELL_LAYOUT_OPTIONS } from "../app-shell/app-shell-layout.token";
import { AppserverBrugerCredentialsRepository, AppserverCredentialsRepository, AppserverInstallationCredentialsRepository } from "../credentials";
import { AuthenticationService } from "./authentication.service";
import { SessionReauthCredentialsService } from "./session-reauth-credentials.service";

export const DEFAULT_MAX_SESSION_LIFETIME_MS = 12 * 60 * 60 * 1000;

/**
 * Fornyer den primære appserver-session (cryptId) stille med session-credentials.
 * Samtidige kald deler én igangværende re-auth (single-flight).
 */
@Injectable({ providedIn: "root" })
export class SessionRefreshService {
    private readonly appShellLayout = inject(APP_SHELL_LAYOUT_OPTIONS, { optional: true });
    private readonly authenticationService = inject(AuthenticationService);
    private readonly credentialsRepository = inject(AppserverCredentialsRepository);
    private readonly brugerCredentials = inject(AppserverBrugerCredentialsRepository);
    private readonly installationCredentials = inject(AppserverInstallationCredentialsRepository);
    private readonly reauthCredentials = inject(SessionReauthCredentialsService);

    private igangvaerende$: Observable<string | null> | null = null;
    /** Gamle cryptIds der allerede er fornyet → ny cryptId. Bruges til requests der var undervejs under re-auth. */
    private readonly erstattedeCryptIds = new Map<string, string>();

    public get aktuelCryptId(): string {
        return (this.brugerCredentials.hent()?.cryptId || this.installationCredentials.hent()?.cryptId || "").trim();
    }

    public kanReAuthenticate(): boolean {
        const credentials = this.reauthCredentials.hent();
        if (!credentials || !this.aktuelCryptId || !this.hentForsyningId()) {
            return false;
        }
        const maxLifetime = this.appShellLayout?.maxSessionLifetimeMs || DEFAULT_MAX_SESSION_LIFETIME_MS;
        return Date.now() - (credentials.loginTidspunkt || 0) < maxLifetime;
    }

    /** Ny cryptId hvis `cryptId` allerede er blevet fornyet, ellers null. */
    public hentFornyetCryptId(cryptId: string): string | null {
        let result = this.erstattedeCryptIds.get(cryptId) || null;
        // Følg kæden hvis der er fornyet flere gange.
        while (result && this.erstattedeCryptIds.has(result)) {
            result = this.erstattedeCryptIds.get(result)!;
        }
        return result && result === this.aktuelCryptId ? result : null;
    }

    /** Returnerer ny cryptId ved succes, ellers null. */
    public reAuthenticate(): Observable<string | null> {
        if (!this.igangvaerende$) {
            this.igangvaerende$ = defer(() => this.udfoerReAuthenticate()).pipe(
                finalize(() => (this.igangvaerende$ = null)),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this.igangvaerende$;
    }

    public nulstil(): void {
        this.erstattedeCryptIds.clear();
    }

    private udfoerReAuthenticate(): Observable<string | null> {
        if (!this.kanReAuthenticate()) {
            return of(null);
        }
        const { brugerNavn, hashedKodeord } = this.reauthCredentials.hent()!;
        const gammelCryptId = this.aktuelCryptId;

        return this.authenticationService.authenticate(brugerNavn, hashedKodeord, this.hentForsyningId()).pipe(
            map((result) => {
                if (!result?.success || !result.cryptId) {
                    return null;
                }
                // Brugeren kan være logget ud mens re-auth stod på.
                if (this.aktuelCryptId !== gammelCryptId) {
                    return null;
                }
                this.credentialsRepository.erstatCryptId(gammelCryptId, result.cryptId);
                this.erstattedeCryptIds.set(gammelCryptId, result.cryptId);
                return result.cryptId;
            }),
            catchError((error) => {
                console.warn("[session-refresh] Stille re-login fejlede", error);
                return of(null);
            })
        );
    }

    private hentForsyningId(): string {
        return (this.brugerCredentials.hent()?.forsyningId || this.installationCredentials.hent()?.forsyningId || "").trim();
    }
}
